import React, { useEffect, useState } from "react";
import { friendlyError } from "../../../middleware/common-functions";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import {
  GET_PRODUCTION_BATCH,
  COMPLETE_PRODUCTION_BATCH,
  CANCEL_PRODUCTION_BATCH,
} from "./queries";
import {
  ProductionBatch,
  PRODUCTION_BATCH_STATUS_LABELS,
  ProductionRecipeInputType,
} from "./models";

type Params = { id: string };

const ProductionBatchDetail: React.FC = () => {
  const { id } = useParams<Params>();
  const history = useHistory();

  const [batch, setBatch] = useState<ProductionBatch | null>(null);
  const [actualOutput, setActualOutput] = useState("");
  const [completeNotes, setCompleteNotes] = useState("");
  const [showCancel, setShowCancel] = useState(false);

  const [fetchBatch, { loading }] = useLazyQuery(GET_PRODUCTION_BATCH, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data.getProductionBatch) {
        setBatch(data.getProductionBatch);
      }
    },
  });

  const [completeBatch, { loading: completing }] = useMutation(
    COMPLETE_PRODUCTION_BATCH,
    {
      onCompleted: () => {
        toast.success(
          lang("Producción completada", "Production completed")
        );
        fetchBatch({ variables: { id } });
      },
      onError: (err) => toast.error(friendlyError(err)),
    }
  );

  const [cancelBatch, { loading: cancelling }] = useMutation(
    CANCEL_PRODUCTION_BATCH,
    {
      onCompleted: () => {
        toast.success(
          lang("Producción cancelada", "Production cancelled")
        );
        fetchBatch({ variables: { id } });
        setShowCancel(false);
      },
      onError: (err) => toast.error(friendlyError(err)),
    }
  );

  useEffect(() => {
    fetchBatch({ variables: { id } });
    //eslint-disable-next-line
  }, [id]);

  const formatDate = (timestamp: string | null) => {
    if (!timestamp) return "—";
    try {
      const date = new Date(parseInt(timestamp));
      return date.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  const handleComplete = () => {
    if (!actualOutput) {
      toast.error(
        lang("Ingresa la cantidad producida", "Enter the output quantity")
      );
      return;
    }
    completeBatch({
      variables: {
        id,
        actual_output: parseFloat(actualOutput),
        notes: completeNotes || null,
      },
    });
  };

  if (loading || !batch) return <LoaderDots fullSize />;

  const recipe = batch.production_recipe;
  const statusInfo = PRODUCTION_BATCH_STATUS_LABELS[batch.status] || {
    label: batch.status,
    labelEn: batch.status,
    color: "secondary",
  };

  const expectedOutput = recipe ? Number(recipe.output_quantity) : 0;
  const efficiency =
    batch.actual_output && expectedOutput
      ? ((Number(batch.actual_output) / expectedOutput) * 100).toFixed(1)
      : null;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          {lang("Lote de Producción", "Production Batch")} #{batch.id}
        </h4>
        <button
          className="btn btn-outline-secondary"
          onClick={() => history.push("/inventory/production")}
        >
          {lang("Volver", "Back")}
        </button>
      </div>

      {/* Batch info */}
      <div className="material_card p-3 mb-3">
        <div className="row">
          <div className="col-lg-4">
            <p className="mb-1">
              <strong>{lang("Receta:", "Recipe:")}</strong>{" "}
              {recipe?.name || "—"}
            </p>
            <p className="mb-1">
              <strong>{lang("Resultado:", "Output:")}</strong>{" "}
              {recipe?.output_ingredient?.name || "—"}
            </p>
            <p className="mb-1">
              <strong>{lang("Sucursal:", "Branch:")}</strong>{" "}
              {batch.branch?.name || "—"}
            </p>
          </div>
          <div className="col-lg-4">
            <p className="mb-1">
              <strong>{lang("Estado:", "Status:")}</strong>{" "}
              <span className={`badge badge-${statusInfo.color}`}>
                {lang(statusInfo.label, statusInfo.labelEn)}
              </span>
            </p>
            <p className="mb-1">
              <strong>{lang("Inicio:", "Started:")}</strong>{" "}
              {formatDate(batch.started_at)}
            </p>
            {batch.completed_at && (
              <p className="mb-1">
                <strong>{lang("Fin:", "Completed:")}</strong>{" "}
                {formatDate(batch.completed_at)}
              </p>
            )}
          </div>
          <div className="col-lg-4">
            <p className="mb-1">
              <strong>{lang("Esperado:", "Expected:")}</strong>{" "}
              {expectedOutput} {recipe?.output_unit || ""}
            </p>
            {batch.actual_output != null && (
              <>
                <p className="mb-1">
                  <strong>{lang("Real:", "Actual:")}</strong>{" "}
                  {Number(batch.actual_output)} {recipe?.output_unit || ""}
                </p>
                <p className="mb-1">
                  <strong>{lang("Eficiencia:", "Efficiency:")}</strong>{" "}
                  {efficiency ? (
                    <span
                      className={
                        parseFloat(efficiency) >= 90
                          ? "text-success font-weight-bold"
                          : parseFloat(efficiency) >= 70
                          ? "text-warning font-weight-bold"
                          : "text-danger font-weight-bold"
                      }
                    >
                      {efficiency}%
                    </span>
                  ) : (
                    "—"
                  )}
                </p>
              </>
            )}
            {batch.notes && (
              <p className="mb-1">
                <strong>{lang("Notas:", "Notes:")}</strong> {batch.notes}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Input ingredients consumed */}
      {recipe?.inputs && recipe.inputs.length > 0 && (
        <div className="material_card p-3 mb-3">
          <h5>{lang("Insumos Consumidos", "Inputs Consumed")}</h5>
          <table className="table table-sm vertical_middle">
            <thead className="thead-light">
              <tr>
                <th>{lang("Ingrediente", "Ingredient")}</th>
                <th className="text-center">
                  {lang("Cantidad", "Quantity")}
                </th>
                <th className="text-center">{lang("Unidad", "Unit")}</th>
                <th className="text-right">{lang("Costo", "Cost")}</th>
              </tr>
            </thead>
            <tbody>
              {recipe.inputs.map((inp: ProductionRecipeInputType) => {
                const cost =
                  inp.ingredient?.cost && inp.quantity
                    ? Number(inp.ingredient.cost) * Number(inp.quantity)
                    : null;
                return (
                  <tr key={inp.id}>
                    <td>{inp.ingredient?.name || `ID ${inp.ingredient_id}`}</td>
                    <td className="text-center">{Number(inp.quantity)}</td>
                    <td className="text-center">{inp.unit}</td>
                    <td className="text-right">
                      {cost != null ? `$${cost.toFixed(2)}` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Complete / Cancel actions */}
      {batch.status === "in_progress" && (
        <div className="material_card p-3 mb-3">
          <h5>{lang("Completar Producción", "Complete Production")}</h5>
          <div className="row">
            <div className="col-lg-3">
              <div className="form-group">
                <label>
                  {lang("Cantidad Producida", "Output Quantity")} *{" "}
                  <small className="text-muted">
                    ({recipe?.output_unit || ""})
                  </small>
                </label>
                <input
                  type="number"
                  step="0.0001"
                  className="form-control"
                  value={actualOutput}
                  onChange={(e) => setActualOutput(e.target.value)}
                  placeholder={String(expectedOutput)}
                />
              </div>
            </div>
            <div className="col-lg-5">
              <div className="form-group">
                <label>{lang("Notas", "Notes")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={completeNotes}
                  onChange={(e) => setCompleteNotes(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="d-flex">
            <button
              className="btn btn-success mr-2"
              onClick={handleComplete}
              disabled={completing}
            >
              {completing ? (
                <i className="fa fa-spinner fa-spin mr-1" />
              ) : (
                <i className="fa fa-check mr-1" />
              )}
              {lang("Completar", "Complete")}
            </button>
            {!showCancel ? (
              <button
                className="btn btn-outline-danger"
                onClick={() => setShowCancel(true)}
              >
                {lang("Cancelar Lote", "Cancel Batch")}
              </button>
            ) : (
              <div className="d-flex align-items-center">
                <span className="text-danger mr-2">
                  {lang(
                    "¿Cancelar y devolver insumos?",
                    "Cancel and return inputs?"
                  )}
                </span>
                <button
                  className="btn btn-danger btn-sm mr-1"
                  onClick={() => cancelBatch({ variables: { id } })}
                  disabled={cancelling}
                >
                  {cancelling ? (
                    <i className="fa fa-spinner fa-spin" />
                  ) : (
                    lang("Sí, cancelar", "Yes, cancel")
                  )}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowCancel(false)}
                >
                  No
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProductionBatchDetail;
